import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Layers,
  Check,
  X,
  Save,
  RotateCcw,
  DollarSign,
  Shield,
  Crown,
  Sparkles,
  Settings2,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const TIER_ICONS = {
  starter: <Layers className="w-6 h-6" />,
  pro: <Sparkles className="w-6 h-6" />,
  studio: <Crown className="w-6 h-6" />,
  legacy: <Shield className="w-6 h-6" />
};

const TIER_COLORS = {
  starter: "from-blue-500/20 to-blue-600/10 border-blue-500/30",
  pro: "from-purple-500/20 to-purple-600/10 border-purple-500/30",
  studio: "from-amber-500/20 to-amber-600/10 border-amber-500/30",
  legacy: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30"
};

const CATEGORY_LABELS = {
  core: "Core Features",
  galleries: "Galleries & Media",
  commerce: "Commerce & Payments",
  scheduling: "Scheduling",
  communication: "Communication",
  team: "Team Features",
  customization: "Customization",
  support: "Support",
  legal: "Legal & Contracts",
  client: "Client Experience",
  business: "Business Tools"
};

const AdminTierManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeTab, setActiveTab] = useState("tiers");

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API}/admin/features`, { headers });
      setConfig(response.data);
      setOriginalConfig(JSON.parse(JSON.stringify(response.data)));
      
      // Expand all categories by default
      const categories = {};
      Object.keys(CATEGORY_LABELS).forEach(cat => categories[cat] = true);
      setExpandedCategories(categories);
    } catch (error) {
      toast.error("Failed to load tier configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.put(`${API}/admin/features`, config, { headers });
      setOriginalConfig(JSON.parse(JSON.stringify(config)));
      setHasChanges(false);
      toast.success("Tier configuration saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
      setConfirmDialogOpen(false);
    }
  };

  const handleReset = () => {
    setConfig(JSON.parse(JSON.stringify(originalConfig)));
    setHasChanges(false);
    toast.info("Changes reverted");
  };

  const updatePlanPrice = (planKey, newPrice) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planKey]: {
          ...prev.plans[planKey],
          monthly_price: parseFloat(newPrice) || 0
        }
      }
    }));
  };

  const updatePlanVisibility = (planKey, showOnPricing) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planKey]: {
          ...prev.plans[planKey],
          show_on_pricing_page: showOnPricing
        }
      }
    }));
  };

  const updatePlanShowPrice = (planKey, showPrice) => {
    setConfig(prev => ({
      ...prev,
      plans: {
        ...prev.plans,
        [planKey]: {
          ...prev.plans[planKey],
          show_price: showPrice
        }
      }
    }));
  };

  const toggleFeatureForPlan = (planKey, featureKey) => {
    setConfig(prev => {
      const currentFeatures = prev.plans[planKey].features || [];
      const hasFeature = currentFeatures.includes(featureKey);
      
      return {
        ...prev,
        plans: {
          ...prev.plans,
          [planKey]: {
            ...prev.plans[planKey],
            features: hasFeature
              ? currentFeatures.filter(f => f !== featureKey)
              : [...currentFeatures, featureKey]
          }
        }
      };
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getFeaturesByCategory = () => {
    if (!config?.feature_definitions) return {};
    
    const grouped = {};
    Object.entries(config.feature_definitions).forEach(([key, feature]) => {
      const category = feature.category || "core";
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push({ key, ...feature });
    });
    return grouped;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load configuration
      </div>
    );
  }

  const featuresByCategory = getFeaturesByCategory();
  const planOrder = ["starter", "pro", "studio", "legacy"];

  return (
    <div className="space-y-8" data-testid="admin-tier-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Tier & Feature Management</h1>
          <p className="text-muted-foreground">Configure subscription plans, pricing, and feature access</p>
        </div>

        <div className="flex gap-3">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} data-testid="reset-changes">
              <RotateCcw className="w-4 h-4 mr-2" />
              Revert
            </Button>
          )}
          <Button
            onClick={() => setConfirmDialogOpen(true)}
            disabled={!hasChanges || saving}
            className="btn-primary"
            data-testid="save-changes"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4 flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-yellow-500" />
          <span className="text-yellow-200">You have unsaved changes. Remember to save before leaving.</span>
        </motion.div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="tiers">Subscription Tiers</TabsTrigger>
          <TabsTrigger value="features">Feature Matrix</TabsTrigger>
        </TabsList>

        {/* Tiers Tab - Visual Card Layout */}
        <TabsContent value="tiers" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {planOrder.map((planKey, index) => {
              const plan = config.plans[planKey];
              if (!plan) return null;

              return (
                <motion.div
                  key={planKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`bg-gradient-to-br ${TIER_COLORS[planKey]} border h-full flex flex-col`}>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="p-2 rounded-sm bg-white/10">
                          {TIER_ICONS[planKey]}
                        </div>
                        {plan.admin_only && (
                          <Badge variant="outline" className="bg-white/10 border-white/20">
                            Admin Only
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-2xl font-serif capitalize">{plan.name}</CardTitle>
                      <CardDescription className="text-muted-foreground">
                        {planKey === "legacy" ? "Special rate for loyal customers" :
                         planKey === "starter" ? "Perfect for getting started" :
                         planKey === "pro" ? "Most popular choice" :
                         "Full studio capabilities"}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex-1 flex flex-col gap-6">
                      {/* Pricing */}
                      <div>
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Price</Label>
                        <div className="flex items-center gap-2 mt-2">
                          <DollarSign className="w-5 h-5 text-muted-foreground" />
                          <Input
                            type="number"
                            value={plan.monthly_price}
                            onChange={(e) => updatePlanPrice(planKey, e.target.value)}
                            className="w-24 text-2xl font-serif bg-white/5 border-white/10"
                            min="0"
                            step="1"
                            data-testid={`price-${planKey}`}
                          />
                          <span className="text-muted-foreground">/mo</span>
                        </div>
                      </div>

                      {/* Visibility Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm">Show on Pricing Page</Label>
                          <p className="text-xs text-muted-foreground">Visible to public</p>
                        </div>
                        <Switch
                          checked={plan.show_on_pricing_page}
                          onCheckedChange={(checked) => updatePlanVisibility(planKey, checked)}
                          disabled={plan.admin_only}
                          data-testid={`visibility-${planKey}`}
                        />
                      </div>

                      {/* Show Price Toggle (Studio only) */}
                      {planKey === "studio" && (
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm">Show Price</Label>
                            <p className="text-xs text-muted-foreground">
                              When off, shows Contact Us
                            </p>
                          </div>
                          <Switch
                            checked={plan.show_price !== false}
                            onCheckedChange={(checked) => updatePlanShowPrice(planKey, checked)}
                            data-testid={`show-price-${planKey}`}
                          />
                        </div>
                      )}

                      {/* Feature Count */}
                      <div className="mt-auto pt-4 border-t border-white/10">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Features Included</span>
                          <Badge variant="secondary" className="bg-white/10">
                            {plan.features?.length || 0}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Commission Rates Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Platform Commission Rates
              </CardTitle>
              <CardDescription>Commission taken from photographer earnings via Stripe Connect</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-4">
                {planOrder.map(planKey => (
                  <div key={planKey} className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-sm text-muted-foreground capitalize">{planKey}</p>
                    <p className="text-2xl font-serif">
                      {config.commission_rates?.[planKey] 
                        ? `${(config.commission_rates[planKey] * 100).toFixed(0)}%`
                        : "N/A"}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                Commission rates are configured in the backend. Contact support to modify.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab - Matrix View */}
        <TabsContent value="features" className="space-y-6">
          {/* Legend */}
          <Card className="bg-card border-border">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-sm bg-green-500/20 border border-green-500/40 flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-sm">Included</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-sm bg-muted/30 border border-muted flex items-center justify-center">
                    <X className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span className="text-sm">Not Included</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Implemented</Badge>
                  <span className="text-sm text-muted-foreground">Feature is live</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Coming Soon</Badge>
                  <span className="text-sm text-muted-foreground">In development</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feature Categories */}
          {Object.entries(CATEGORY_LABELS).map(([category, label]) => {
            const features = featuresByCategory[category] || [];
            if (features.length === 0) return null;

            const isExpanded = expandedCategories[category];

            return (
              <Card key={category} className="bg-card border-border overflow-hidden">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
                  onClick={() => toggleCategory(category)}
                >
                  <h3 className="text-lg font-medium">{label}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{features.length} features</Badge>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left p-3 text-sm font-medium text-muted-foreground w-1/3">Feature</th>
                            {planOrder.map(planKey => (
                              <th key={planKey} className="text-center p-3 text-sm font-medium text-muted-foreground capitalize">
                                {planKey}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {features.map((feature, idx) => (
                            <tr key={feature.key} className={idx % 2 === 0 ? "bg-muted/10" : ""}>
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{feature.name}</span>
                                  {feature.implemented ? (
                                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                                      Live
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">
                                      Soon
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                              </td>
                              {planOrder.map(planKey => {
                                const plan = config.plans[planKey];
                                const hasFeature = plan?.features?.includes(feature.key);
                                return (
                                  <td key={planKey} className="text-center p-3">
                                    <button
                                      onClick={() => toggleFeatureForPlan(planKey, feature.key)}
                                      className={`w-8 h-8 rounded-sm flex items-center justify-center mx-auto transition-all hover:scale-110 ${
                                        hasFeature
                                          ? "bg-green-500/20 border border-green-500/40 text-green-400"
                                          : "bg-muted/30 border border-muted text-muted-foreground hover:border-muted-foreground"
                                      }`}
                                      data-testid={`feature-toggle-${planKey}-${feature.key}`}
                                    >
                                      {hasFeature ? (
                                        <Check className="w-4 h-4" />
                                      ) : (
                                        <X className="w-4 h-4" />
                                      )}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Confirm Save Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Configuration Changes</DialogTitle>
            <DialogDescription>
              This will update the subscription tiers and feature access for all users. Changes will be reflected on the public pricing page immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">Summary of changes:</p>
            <ul className="space-y-2 text-sm">
              {planOrder.map(planKey => {
                const original = originalConfig?.plans?.[planKey];
                const current = config?.plans?.[planKey];
                if (!original || !current) return null;

                const changes = [];
                if (original.monthly_price !== current.monthly_price) {
                  changes.push(`Price: $${original.monthly_price} → $${current.monthly_price}`);
                }
                if (original.show_on_pricing_page !== current.show_on_pricing_page) {
                  changes.push(`Visibility: ${current.show_on_pricing_page ? "Show" : "Hide"}`);
                }
                const addedFeatures = current.features?.filter(f => !original.features?.includes(f)) || [];
                const removedFeatures = original.features?.filter(f => !current.features?.includes(f)) || [];
                if (addedFeatures.length) changes.push(`+${addedFeatures.length} features`);
                if (removedFeatures.length) changes.push(`-${removedFeatures.length} features`);

                if (changes.length === 0) return null;

                return (
                  <li key={planKey} className="flex items-start gap-2">
                    <span className="font-medium capitalize">{planKey}:</span>
                    <span className="text-muted-foreground">{changes.join(", ")}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? "Saving..." : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTierManagement;
